import { Modal, Table, ColumnDefinition, RowDefinition } from '@indigo-ag/component-module';
import { getBackgroundCheckStepEvents } from 'features/brazil-grower/queries/backgroundCheck.queries';
import { BackgroundCheckStepEvent, BackgroundCheckStepType, BackgroundCheckStepEventList } from '@indigo-ag/schema';
import React from 'react';
import { utils } from 'shared';
import styled from 'styled-components';
import { getDetailsModalTitle, getEventTypeTranslation, getCommentTranslation } from '../../utils/backgroundCheck.util';
import i18next from 'i18next';
import { useQuery } from '@apollo/client';

const EventsContainer = styled.div`
  margin-bottom: 16px;
`;

const EventsModal = styled(Modal)`
  min-width: 750px;
  padding-bottom: 16px;
  overflow: scroll;
  max-height: 600px;
`;

const EventCell = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 42px;
  position: relative;
  min-width: 0;
  padding: 12px 0;
`;

const Event = styled.div`
  font-size: 1rem;
  line-height: 24px;
  font-weight: 400;
  color: #303c41;
  overflow: visible;
  overflow-wrap: break-word;
`;

const EventComment = styled.div`
  font-size: 0.875rem;
  font-weight: 400;
  color: #5b6b72;
  overflow: visible;
  overflow-wrap: break-word;
`;

const EVENTS_PAGE_SIZE = 5;

interface Props {
  stepId: string;
  stepType: BackgroundCheckStepType;
  isOpen: boolean;
  onRequestClose: () => void;
}

interface EventsQueryResponse {
  backgroundCheckStepEvents: BackgroundCheckStepEventList;
}

export type ColumnId = 'eventType' | 'eventTime';

export const BackgroundCheckStepEventsModal = ({ stepId, stepType, isOpen, onRequestClose }: Props) => {
  const { data, error, fetchMore, loading } = useQuery<EventsQueryResponse>(getBackgroundCheckStepEvents, {
    variables: { stepId, limit: EVENTS_PAGE_SIZE, offset: 0 }
  });

  const backgroundCheckStepEvents = data?.backgroundCheckStepEvents.backgroundCheckStepEvents;
  const pageInfo = data?.backgroundCheckStepEvents.pageInfo;

  const columnDefinition: ColumnDefinition<BackgroundCheckStepEvent, ColumnId>[] = [
    {
      id: 'eventType',
      header: i18next.t('backgroundCheckDetailsTableHeader_EventType'),
      cell: ({ row }) => (
        <EventCell>
          <Event>{getEventTypeTranslation(row.eventType)}</Event>
          <EventComment>{row.comment && getCommentTranslation(row.comment)}</EventComment>
        </EventCell>
      )
    },
    {
      id: 'eventTime',
      header: i18next.t('backgroundCheckDetailsTableHeader_EventTime'),
      cell: ({ row, SubtitleCell }) => <SubtitleCell label={utils.date.formatDate(row.createdAt, 'L LT')} />
    }
  ];

  const rowDefinition: RowDefinition<BackgroundCheckStepEvent, ColumnId> = {
    rowId: ({ row }) => row.id
  };

  const content = (
    <EventsContainer>
      <Table
        label="Events"
        tableStyle="inline"
        loading={loading}
        error={!!error}
        columnDefinition={columnDefinition}
        rowDefinition={rowDefinition}
        data={backgroundCheckStepEvents}
        pagination={pageInfo}
        paginationStyle="show-more"
        onTableAction={action => {
          switch (action.type) {
            case 'SHOW_MORE':
              fetchMore({
                variables: {
                  stepId,
                  offset: 0,
                  limit: pageInfo?.limit ? pageInfo?.limit + EVENTS_PAGE_SIZE : EVENTS_PAGE_SIZE
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                  if (fetchMoreResult) {
                    const newResult: EventsQueryResponse = {
                      backgroundCheckStepEvents: {
                        backgroundCheckStepEvents: previousResult.backgroundCheckStepEvents.backgroundCheckStepEvents.concat(
                          fetchMoreResult.backgroundCheckStepEvents.backgroundCheckStepEvents
                        ),
                        pageInfo: fetchMoreResult.backgroundCheckStepEvents.pageInfo
                      }
                    };
                    return newResult;
                  }
                  return previousResult;
                }
              });
              return;
          }
        }}
      />
    </EventsContainer>
  );
  return (
    <EventsModal
      onRequestClose={onRequestClose}
      renderContent={() => content}
      open={isOpen}
      title={getDetailsModalTitle(stepType)}
    />
  );
};
